import {useRideLanguage, type RideLanguage} from '../ride-language';
import {useEffect, useId, useRef, useState} from 'react';

export type RidePlace = {name: string; lat: number; lng: number};
interface GoogleAutocomplete {
  addListener(event: string, listener: () => void): {remove(): void};
  getPlace(): {formatted_address?: string; name?: string; geometry?: {location?: {lat(): number; lng(): number}}};
}
declare global {
  interface Window {
    google?: {maps: {places: {Autocomplete: new (input: HTMLInputElement, options: {fields: string[]}) => GoogleAutocomplete}}};
  }
}

let placesPromise: Promise<void> | undefined;
function loadPlaces(language: RideLanguage): Promise<void> {
  if (window.google?.maps?.places) return Promise.resolve();
  if (placesPromise) return placesPromise;
  placesPromise = new Promise<void>((resolve, reject) => {
    const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!key) { reject(new Error('Location search is unavailable.')); return; }
    const existing = document.querySelector<HTMLScriptElement>('script[src*="maps.googleapis.com/maps/api/js"]');
    const script = existing ?? document.createElement('script');
    const finish = (error?: Error) => {
      clearTimeout(timeout);
      script.removeEventListener('load', loaded);
      script.removeEventListener('error', failed);
      error ? reject(error) : resolve();
    };
    const loaded = () => finish(window.google?.maps?.places ? undefined : new Error('Location search is unavailable.'));
    const failed = () => finish(new Error('Location search could not load. Refresh and try again.'));
    const timeout = setTimeout(failed, 15000);
    script.addEventListener('load', loaded);
    script.addEventListener('error', failed);
    if (!existing) {
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&v=quarterly&language=${language}`;
      script.async = true;
      document.head.appendChild(script);
    }
  });
  return placesPromise;
}

export function LocationField({label, value, onChange, error, required = false}: {
  label: string; value?: RidePlace; onChange: (place?: RidePlace) => void; error?: string; required?: boolean;
}) {
  const {t, language} = useRideLanguage();
  const placesLanguage = useRef(language);
  placesLanguage.current = language;
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const change = useRef(onChange);
  change.current = onChange;
  const [active, setActive] = useState(false);
  const [ready, setReady] = useState(false);
  const [failure, setFailure] = useState('');
  useEffect(() => {
    if (input.current && value) input.current.value = value.name;
  }, [value?.name]);
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let listener: {remove(): void} | undefined;
    void loadPlaces(placesLanguage.current).then(() => {
      if (cancelled || !input.current || !window.google) return;
      const autocomplete = new window.google.maps.places.Autocomplete(input.current, {fields: ['formatted_address', 'geometry', 'name']});
      listener = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const point = place.geometry?.location;
        change.current(point ? {name: place.formatted_address || place.name || input.current!.value, lat: point.lat(), lng: point.lng()} : undefined);
      });
      setReady(true);
    }).catch((e: Error) => { if (!cancelled) setFailure(e.message); });
    return () => { cancelled = true; listener?.remove(); };
  }, [active]);
  return <label className="ride-field" htmlFor={id}>{t(label)}
    <input id={id} ref={input} dir="auto" className="ride-input" placeholder={t("Enter an address or place")} autoComplete="off"
      required={required} aria-invalid={!!error} aria-describedby={`${id}-hint`} onFocus={() => setActive(true)}
      onChange={() => { if (value) onChange(undefined); }} />
    <span id={`${id}-hint`} className="ride-help" role={error || failure ? "alert" : undefined} style={error || failure ? {color: '#9b3426'} : undefined}>
      {t(error || failure || (active && !ready ? 'Loading location suggestions…' : 'Choose an address from the suggestions.'))}
    </span>
  </label>;
}
